import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Layers, Loader2, CalendarIcon, Send, Trash2, X, Plus, ArrowRight, PackageCheck } from "lucide-react";
import { getBatches, getBatch, setBatchProcessingDate, postNetcashBatch, deleteBatch, removeInvoiceFromBatch } from "../../services/dataService";
import { PageHeader, StatCard } from "../PageHeader";
import { StatusBadge } from "../StatusBadge";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { formatApiErrorDetail } from "../../lib/api";
import { formatCurrency, formatDate } from "../../lib/format";
import { toast } from "sonner";

function BatchDetailDialog({ batchId, open, onClose }) {
  const qc = useQueryClient();
  const [calOpen, setCalOpen] = useState(false);
  const { data: batch, isLoading } = useQuery({ queryKey: ["batch", batchId], queryFn: () => getBatch(batchId), enabled: !!batchId && open });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["batch", batchId] });
    qc.invalidateQueries({ queryKey: ["batches"] });
    qc.invalidateQueries({ queryKey: ["invoices"] });
    qc.invalidateQueries({ queryKey: ["stats"] });
    qc.invalidateQueries({ queryKey: ["receipts"] });
  };

  const dateMut = useMutation({
    mutationFn: (d) => setBatchProcessingDate(batchId, d),
    onSuccess: () => { invalidate(); toast.success("Processing date set"); },
  });

  const removeMut = useMutation({
    mutationFn: (invId) => removeInvoiceFromBatch(batchId, invId),
    onSuccess: () => { invalidate(); toast.success("Invoice removed from batch"); },
  });

  const sendMut = useMutation({
    mutationFn: () => postNetcashBatch(batchId),
    onSuccess: (data) => { invalidate(); toast.success(`Submitted to Netcash — ref ${data.netcash_ref} (${data.orders} orders)`); onClose(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send to Netcash failed"),
  });

  const isOpen = batch?.status === "Open";
  const hasDate = !!batch?.processing_date;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="batch-detail-dialog">
        {isLoading || !batch ? (
          <>
            <DialogHeader>
              <DialogTitle className="sr-only">Batch details</DialogTitle>
              <DialogDescription className="sr-only">Loading batch…</DialogDescription>
            </DialogHeader>
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                <Layers className="h-5 w-5 text-blue-600" /> {batch.name}
                <StatusBadge status={batch.status} />
              </DialogTitle>
              <DialogDescription>
                {batch.invoice_count} invoice(s) · Total {formatCurrency(batch.total_amount)} · Created {formatDate(batch.created_at)}
                {batch.netcash_ref && <> · Netcash ref <span className="font-mono">{batch.netcash_ref}</span></>}
              </DialogDescription>
            </DialogHeader>

            {/* Processing date + send controls */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 bg-slate-50/60 dark:bg-slate-800/40">
              <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                <div className="flex-1">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Processing Date (required)</label>
                  <Popover open={calOpen} onOpenChange={setCalOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" disabled={!isOpen} className="mt-1.5 w-full justify-start text-left font-normal" data-testid="processing-date-trigger">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {hasDate ? formatDate(batch.processing_date) : <span className="text-slate-400">Pick a processing date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={batch.processing_date ? new Date(batch.processing_date) : undefined}
                        onSelect={(d) => { if (d) { dateMut.mutate(format(d, "yyyy-MM-dd")); setCalOpen(false); } }}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <Button
                  onClick={() => sendMut.mutate()}
                  disabled={!isOpen || !hasDate || sendMut.isPending || batch.invoice_count === 0}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold h-10"
                  data-testid="send-to-netcash-button"
                >
                  {sendMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Send className="h-4 w-4 mr-2" /> Send to Netcash</>)}
                </Button>
              </div>
              {isOpen && !hasDate && <p className="mt-2 text-xs text-amber-600">A processing date must be chosen before you can send to Netcash.</p>}
            </div>

            {/* Invoice list */}
            <div className="mt-2 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/80 dark:bg-slate-800/60">
                    <TableHead className="text-xs uppercase tracking-wider">Invoice</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Client</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">Amount</TableHead>
                    {isOpen && <TableHead className="w-12" />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batch.invoices.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="h-24 text-center text-sm text-slate-500">No invoices in this batch.</TableCell></TableRow>
                  ) : batch.invoices.map((inv) => (
                    <TableRow key={inv.id} data-testid={`batch-invoice-${inv.id}`}>
                      <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                      <TableCell className="text-sm">{inv.client_name}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(inv.amount)}</TableCell>
                      {isOpen && (
                        <TableCell>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-rose-600" onClick={() => removeMut.mutate(inv.id)} data-testid={`remove-invoice-${inv.id}`}>
                            <X className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function BatchesManager() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState(null);
  const { data: batches = [], isLoading } = useQuery({ queryKey: ["batches"], queryFn: getBatches });

  const delMut = useMutation({
    mutationFn: deleteBatch,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["batches"] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success("Batch deleted; invoices returned to awaiting");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"),
  });

  const openBatches = batches.filter((b) => b.status === "Open");
  const submitted = batches.filter((b) => b.status !== "Open");

  return (
    <div>
      <PageHeader title="Batches" icon={Layers} description="Staging area for debit order batches. Open a batch, choose a processing date, then send it to Netcash." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Batches" value={batches.length} accent="slate" />
        <StatCard label="Open (Staging)" value={openBatches.length} accent="amber" />
        <StatCard label="Submitted" value={submitted.length} accent="blue" />
        <StatCard label="Open Value" value={formatCurrency(openBatches.reduce((s, b) => s + b.total_amount, 0))} accent="emerald" />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
      ) : batches.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-12 text-center" data-testid="batches-empty">
          <PackageCheck className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">No batches yet. Go to <span className="font-semibold">Invoices</span>, select invoices and click <span className="font-semibold">Add to Batch</span>.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="batches-grid">
          {batches.map((b) => (
            <div key={b.id} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`batch-card-${b.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-display font-semibold text-slate-900 dark:text-slate-50">{b.name}</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Created {formatDate(b.created_at)}</p>
                </div>
                <StatusBadge status={b.status} />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400">Invoices</div>
                  <div className="font-semibold text-slate-800 dark:text-slate-100">{b.invoice_count}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400">Total</div>
                  <div className="font-mono font-semibold text-slate-800 dark:text-slate-100">{formatCurrency(b.total_amount)}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs uppercase tracking-wider text-slate-400">Processing Date</div>
                  <div className={`font-semibold ${b.processing_date ? "text-slate-800 dark:text-slate-100" : "text-amber-600"}`}>
                    {b.processing_date ? formatDate(b.processing_date) : "Not set"}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <Button onClick={() => setOpenId(b.id)} className="flex-1 bg-slate-900 hover:bg-slate-800 dark:bg-blue-600 text-white" data-testid={`open-batch-${b.id}`}>
                  {b.status === "Open" ? "Manage & Send" : "View"} <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
                {b.status === "Open" && (
                  <Button variant="outline" size="icon" className="text-slate-400 hover:text-rose-600" onClick={() => delMut.mutate(b.id)} data-testid={`delete-batch-${b.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <BatchDetailDialog batchId={openId} open={!!openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
